import type { MicroService } from "@/types";
import { resolveSecureMicroserviceUrl } from "@/utils/resolveSecureMicroserviceUrl";

export type UserFlags = {
  isSuperAdmin?: boolean;
  is_super_admin?: boolean;
  roles?: string[];
};

function rolesLookLikeCrmAdmin(roleCodes: string[]): boolean {
  return roleCodes.some(
    (r) => typeof r === "string" && /crm/i.test(r) && /admin/i.test(r),
  );
}

function permissionSuggestsOrgOrCrm(p: string): boolean {
  const x = p.toLowerCase();
  return (
    x.startsWith("msorg:") ||
    x.startsWith("ms_org:") ||
    x.startsWith("mscrm:")
  );
}

/**
 * 是否应在侧栏显示 msOrg（含：msorg/ms_org/mscrm 前缀权限，或 CRM*ADMIN* 类角色名）。
 * CRM 管理员常用组织员工能力，但角色可能只配了 msdevice 等，故用角色名作补充推断。
 */
export function userHasMsorgMenuAccess(
  userData: UserFlags,
  userPermissions: string[],
): boolean {
  if (userData.isSuperAdmin === true || userData.is_super_admin === true) {
    return true;
  }
  if (userPermissions.includes("*")) {
    return true;
  }
  if (
    userPermissions.some(
      (p) => typeof p === "string" && permissionSuggestsOrgOrCrm(p),
    )
  ) {
    return true;
  }
  if (rolesLookLikeCrmAdmin(userData.roles ?? [])) {
    return true;
  }
  return false;
}

function normalizeUrlLoose(url: string): string {
  if (!url) return "";
  const isHttpsPage =
    typeof window !== "undefined" && window.location.protocol === "https:";
  if (/^https?:\/\//i.test(url)) {
    if (isHttpsPage && /^http:\/\//i.test(url)) {
      return url.replace(/^http:\/\//i, "https://");
    }
    return url;
  }
  return `${isHttpsPage ? "https" : "http"}://${url}`;
}

/**
 * DB 未注册服务时，用户已有对应 operation（如 msorg:user:view）仍应出现菜单。
 * 默认入口域名可按环境变量覆盖。
 */
const PERMISSION_FALLBACK_SERVICES: Record<
  string,
  { displayName: string; defaultUrl: string; envUrlKey: string; menuOrder: number }
> = {
  msorg: {
    displayName: "组织及用户服务",
    defaultUrl: "https://org.ihoms.cn",
    envUrlKey: "VITE_MSORG_ORIGIN",
    menuOrder: 60,
  },
  mscrm: {
    displayName: "客户关系管理",
    defaultUrl: "https://crm.ihoms.cn",
    envUrlKey: "VITE_MSCRM_ORIGIN",
    menuOrder: 70,
  },
};

let fallbackIdSeq = 0;

function userHasServicePermissionPrefix(
  slug: string,
  userPermissions: string[],
  userData: UserFlags,
): boolean {
  if (userData.isSuperAdmin === true || userData.is_super_admin === true) {
    return true;
  }
  if (userPermissions.includes("*")) {
    return true;
  }
  const prefix = `${slug.toLowerCase()}:`;
  return userPermissions.some(
    (p) => typeof p === "string" && p.toLowerCase().startsWith(prefix),
  );
}

/**
 * 在 API 返回列表基础上，为「有权限但 services 表未登记」的已知服务补一条菜单项。
 */
export function appendFallbackMicroservicesFromPermissions(
  services: MicroService[],
  userPermissions: string[],
  userData: UserFlags,
): MicroService[] {
  const existing = new Set(services.map((s) => s.name.toLowerCase()));
  const out = [...services];

  for (const [slug, def] of Object.entries(PERMISSION_FALLBACK_SERVICES)) {
    if (existing.has(slug)) continue;
    const allow =
      slug === "msorg"
        ? userHasMsorgMenuAccess(userData, userPermissions)
        : userHasServicePermissionPrefix(slug, userPermissions, userData);
    if (!allow) {
      continue;
    }

    const viteEnv = (import.meta as unknown as { env?: Record<string, string> })
      .env;
    const env = String(viteEnv?.[def.envUrlKey] ?? "").trim();
    const rawUrl = env || def.defaultUrl;
    const normalized = normalizeUrlLoose(rawUrl);
    const secureFront =
      resolveSecureMicroserviceUrl(slug, {
        url: normalized,
        routePath: `/${slug}`,
        name: slug,
      }) || normalized;

    fallbackIdSeq += 1;
    out.push({
      id: `fallback-${slug}-${fallbackIdSeq}`,
      name: slug,
      displayName: def.displayName,
      description: "",
      url: secureFront,
      status: "online",
      version: "1.0.0",
      icon: "api",
      color: "#1890ff",
      permissions: [`${slug}:all:view`],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      routePath: `/${slug}`,
      isMenuItem: true,
      menuOrder: def.menuOrder,
      requiresAuth: true,
      isExternal: false,
      serviceUrl: secureFront,
      isSystemService: false,
      serviceType: 1,
    });
  }

  return out;
}

/**
 * msadmin 管理台：全局 * 不视为拥有管理台入口（常见「业务全量 *」无法进子站时仍不应出现齿轮）。
 * 超管在上层已放行。
 * 库表 required 仅当条目为「*」或以 msadmin: 开头时才参与精确匹配，避免误配 msdevice: 等导致齿轮误开。
 */
function userCanAccessMsadminMicroservice(
  service: Pick<MicroService, "permissions">,
  userPermissions: string[],
): boolean {
  const required =
    service.permissions && service.permissions.length > 0
      ? service.permissions
      : [];

  if (required.length > 0) {
    if (
      required.some((req) => {
        if (typeof req !== "string" || !userPermissions.includes(req)) {
          return false;
        }
        const r = req.toLowerCase();
        return r === "*" || r.startsWith("msadmin:");
      })
    ) {
      return true;
    }
  }
  return userPermissions.some(
    (p) => typeof p === "string" && p.toLowerCase().startsWith("msadmin:"),
  );
}

/**
 * 是否允许当前用户在 msLogin 侧看到并打开该微服务（与菜单过滤一致）。
 * - 超级管理员：全部可见
 * - msadmin：不因全局 * 自动可见，须显式 msadmin: 或库表 required 命中（见 userCanAccessMsadminMicroservice）
 * - 其他服务：permissions 含 * 则全部可见
 * - 与 required_permissions 任一精确命中：可见
 * - 未配置 required_permissions 时：除 msadmin 外默认可见
 * - 否则：用户任一 permission 以「服务名:」为前缀则可见
 */
export function userCanAccessMicroservice(
  service: Pick<MicroService, "name" | "permissions" | "isSystemService">,
  userData: UserFlags,
  userPermissions: string[],
): boolean {
  if (userData.isSuperAdmin === true || userData.is_super_admin === true) {
    return true;
  }

  const snEarly = (service.name || "").trim().toLowerCase();
  if (snEarly === "msadmin") {
    return userCanAccessMsadminMicroservice(service, userPermissions);
  }

  if (userPermissions.includes("*")) {
    return true;
  }

  const required =
    service.permissions && service.permissions.length > 0
      ? service.permissions
      : [];

  const serviceNameEarly = (service.name || "").trim().toLowerCase();

  if (required.length === 0) {
    if (serviceNameEarly !== "msadmin") {
      return true;
    }
  } else if (required.some((req) => userPermissions.includes(req))) {
    return true;
  }

  const serviceName = (service.name || "").trim();
  if (!serviceName) {
    return false;
  }
  const sn = serviceName.toLowerCase();
  if (sn === "msorg" && userHasMsorgMenuAccess(userData, userPermissions)) {
    return true;
  }
  const prefixes =
    sn === "msorg" ? ["msorg:", "ms_org:"] : [`${sn}:`];
  return userPermissions.some((p) => {
    if (typeof p !== "string") return false;
    const pl = p.toLowerCase();
    return prefixes.some((pref) => pl.startsWith(pref));
  });
}
